/** AI coach (0.9.7): today's note and a chat that can look at the athlete's own data. Shown only when
 * the server has the AI set up (`/me.features.ai`); without the rider's AI consent it asks for it first.
 * Otherwise the server's sentence says what's missing (402/409). */
import { useState, type FormEvent } from "react";
import { ApiError } from "@/api/client";
import { useAccount, useChatSend, useCoachNote, useSetConsent } from "@/api/hooks";
import { useToast } from "@/components/Toast";
import { Card } from "@/components/ui";

const LOOKED: Record<string, string> = {
  get_plan: "your plan",
  list_activities: "your rides",
  get_fitness: "your fitness",
  get_weeks: "your weeks",
};

type Line = { role: "user" | "assistant"; text: string; looked?: string[] };

function AskConsent() {
  const consent = useSetConsent();
  const toast = useToast();
  return (
    <Card title="Ask your coach">
      <div className="stack" style={{ gap: 10 }} data-testid="coach-consent">
        <p style={{ margin: 0 }}>
          The AI coach answers questions about your training and writes a short note each morning. To do that,
          your plan, rides and fitness go to Claude (Anthropic); they are never used to train any model.
        </p>
        <div>
          <button
            className="primary"
            disabled={consent.isPending}
            onClick={() =>
              consent.mutateAsync({ kind: "ai", granted: true }).then(
                () => toast.ok("AI coach on"),
                (e: unknown) => toast.error(e),
              )
            }
          >
            Turn on the AI coach
          </button>
        </div>
      </div>
    </Card>
  );
}

export function CoachChat() {
  const account = useAccount();
  if (!account.data) return null;
  if (!account.data.consents.ai?.granted) return <AskConsent />;
  return <Chat />;
}

function Chat() {
  const note = useCoachNote();
  const send = useChatSend();
  const [conv, setConv] = useState<string | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [draft, setDraft] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const message = draft.trim();
    if (!message) return;
    setProblem(null);
    setLines((l) => [...l, { role: "user", text: message }]);
    setDraft("");
    try {
      const r = await send.mutateAsync({ message, conversation_id: conv });
      setConv(r.conversation_id);
      setLines((l) => [...l, { role: "assistant", text: r.reply, looked: r.looked_at }]);
    } catch (err) {
      setLines((l) => l.slice(0, -1));
      setDraft(message);
      setProblem(err instanceof ApiError ? err.message : "The coach could not answer right now.");
    }
  }

  return (
    <Card title="Ask your coach">
      <div className="stack" style={{ gap: 10 }} data-testid="coach-chat">
        {note.data && (
          <p className="coach-note" data-testid="coach-note">
            {note.data.text}
          </p>
        )}
        {lines.length > 0 && (
          <ol className="chat" aria-live="polite">
            {lines.map((l, i) => (
              <li key={i} className={`chat-line ${l.role}`}>
                {l.text}
                {l.looked && l.looked.length > 0 && (
                  <small className="faint">
                    {" "}
                    · looked at {l.looked.map((x) => LOOKED[x] ?? x).join(", ")}
                  </small>
                )}
              </li>
            ))}
          </ol>
        )}
        {problem && (
          <p className="badge warn" role="alert" style={{ margin: 0 }}>
            {problem}
          </p>
        )}
        <form className="row" style={{ gap: 8 }} onSubmit={submit}>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. Why is Saturday so long?"
            aria-label="Message to the coach"
            maxLength={2000}
            style={{ flex: 1 }}
          />
          <button className="primary" type="submit" disabled={send.isPending || !draft.trim()}>
            {send.isPending ? "Thinking…" : "Ask"}
          </button>
        </form>
      </div>
    </Card>
  );
}
